/**
 * The first edition, "Estreia" (NEWS-28…36, NEWS-38, NEWS-44): its 9 slides
 * in order with their durations and torus spots, the archive of the 15 news
 * since 15 Oct (editions 02 and 01, newest first) with the slide each "Ver"
 * opens, and the featured card's highlights.
 */
import type {
  ArchiveItem,
  EditionModule,
  I18nText,
  NewsArea,
} from '@/lib/whats-new/edition-types';
import { AnalyticsSlide } from '@/components/whats-new/editions/estreia/analytics-slide';
import { AppSlide } from '@/components/whats-new/editions/estreia/app-slide';
import { CoverSlide } from '@/components/whats-new/editions/estreia/cover-slide';
import { CsvSlide } from '@/components/whats-new/editions/estreia/csv-slide';
import { HomeSlide } from '@/components/whats-new/editions/estreia/home-slide';
import { MoreSlide } from '@/components/whats-new/editions/estreia/more-slide';
import { OutroSlide } from '@/components/whats-new/editions/estreia/outro-slide';
import { PhoneSlide } from '@/components/whats-new/editions/estreia/phone-slide';
import { StudioSlide } from '@/components/whats-new/editions/estreia/studio-slide';

export const ESTREIA_ID = 'estreia';

/** Index of each slide in the player (0-based; the spec counts from 1). */
export const ESTREIA_SLIDE = {
  cover: 0,
  phone: 1,
  csv: 2,
  studio: 3,
  home: 4,
  analytics: 5,
  app: 6,
  more: 7,
  outro: 8,
} as const;

const item = (
  area: NewsArea,
  text: I18nText,
  slide: keyof typeof ESTREIA_SLIDE
): ArchiveItem => ({ area, text, slideIndex: ESTREIA_SLIDE[slide] });

export const estreiaEdition: EditionModule = {
  id: ESTREIA_ID,
  slides: [
    {
      id: 'cover',
      durationMs: 8500,
      torusSpot: 'cover',
      Component: CoverSlide,
    },
    {
      id: 'phone',
      durationMs: 11000,
      torusSpot: 'content',
      Component: PhoneSlide,
    },
    { id: 'csv', durationMs: 11000, torusSpot: 'content', Component: CsvSlide },
    {
      id: 'studio',
      durationMs: 12000,
      torusSpot: 'content',
      Component: StudioSlide,
    },
    {
      id: 'home',
      durationMs: 11000,
      torusSpot: 'content',
      Component: HomeSlide,
    },
    {
      id: 'analytics',
      durationMs: 12500,
      torusSpot: 'content',
      Component: AnalyticsSlide,
    },
    { id: 'app', durationMs: 10500, torusSpot: 'content', Component: AppSlide },
    {
      id: 'more',
      durationMs: 10000,
      torusSpot: 'content',
      Component: MoreSlide,
    },
    {
      id: 'outro',
      durationMs: null,
      torusSpot: 'outro',
      Component: OutroSlide,
    },
  ],
  archive: [
    {
      number: '02',
      dates: (LL) => LL.whatsNew.editions.estreia.archive.edition02.dates(),
      title: (LL) => LL.whatsNew.editions.estreia.archive.edition02.title(),
      items: [
        item(
          'home',
          (LL) => LL.whatsNew.editions.estreia.archive.edition02.items.home(),
          'home'
        ),
        item(
          'analytics',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition02.items.analyticsTabs(),
          'analytics'
        ),
        item(
          'analytics',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition02.items.analyticsDetails(),
          'analytics'
        ),
        item(
          'app',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition02.items.couponContrast(),
          'app'
        ),
        item(
          'app',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition02.items.appPage(),
          'app'
        ),
        item(
          'app',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition02.items.mobileSignup(),
          'more'
        ),
      ],
    },
    {
      number: '01',
      dates: (LL) => LL.whatsNew.editions.estreia.archive.edition01.dates(),
      title: (LL) => LL.whatsNew.editions.estreia.archive.edition01.title(),
      items: [
        item(
          'campaigns',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.csvImport(),
          'csv'
        ),
        item(
          'home',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.mobilePanel(),
          'phone'
        ),
        item(
          'analytics',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.mobileAnalytics(),
          'phone'
        ),
        item(
          'campaigns',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.campaignCards(),
          'more'
        ),
        item(
          'campaigns',
          (LL) => LL.whatsNew.editions.estreia.archive.edition01.items.studio(),
          'studio'
        ),
        item(
          'campaigns',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.testPush(),
          'more'
        ),
        item(
          'app',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.visualEditors(),
          'app'
        ),
        item(
          'audiences',
          (LL) =>
            LL.whatsNew.editions.estreia.archive.edition01.items.audiences(),
          'more'
        ),
        item(
          'team',
          (LL) => LL.whatsNew.editions.estreia.archive.edition01.items.team(),
          'more'
        ),
      ],
    },
  ],
  highlights: [
    {
      label: (LL) => LL.whatsNew.editions.estreia.highlights.mobileLabel(),
      text: (LL) => LL.whatsNew.editions.estreia.highlights.mobile(),
    },
    {
      label: (LL) => LL.whatsNew.areas.campaigns(),
      text: (LL) => LL.whatsNew.editions.estreia.highlights.campaigns(),
    },
    {
      label: (LL) => LL.whatsNew.areas.home(),
      text: (LL) => LL.whatsNew.editions.estreia.highlights.home(),
    },
    {
      label: (LL) => LL.whatsNew.areas.analytics(),
      text: (LL) => LL.whatsNew.editions.estreia.highlights.analytics(),
    },
    {
      label: (LL) => LL.whatsNew.areas.app(),
      text: (LL) => LL.whatsNew.editions.estreia.highlights.app(),
    },
  ],
  featured: {
    eyebrow: (LL) => LL.whatsNew.editions.estreia.featured.eyebrow(),
    title: (LL) => LL.whatsNew.editions.estreia.featured.title(),
    watch: (LL) => LL.whatsNew.editions.estreia.featured.watch(),
    rewatch: (LL) => LL.whatsNew.editions.estreia.featured.rewatch(),
  },
};
